import React, { useEffect, useRef, useState } from 'react';
import { useGasI18n, useGasNotify } from '../../shared/kit';
import {
    configureApi,
    createReservation,
    fetchAvailability,
    fetchCalendar,
    fetchItem,
    fetchOpeningHours,
    fingerprintReserveAttempt,
    joinWaitlist,
    newIdempotencyKey,
    resolveAttemptKey,
} from './api';
import type { AttemptKeyState, OpeningHours, ReserveItemDetail } from './api';
import { AvailabilityCalendar } from './components/AvailabilityCalendar';
import type { PickupMode, RentalAvailability, RentalCalendar, RentalReserveInitData } from './types';

function pad(n: number): string { return String(n).padStart(2, '0'); }
function ymd(d: Date): string { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function serverDateTime(d: Date | null, time: string): string {
    if (!d) return '';
    const tt = /^\d{2}:\d{2}$/.test(time) ? time : '09:00';
    return `${ymd(d)} ${tt}:00`;
}
function fmtDate(d: Date | null): string {
    return d ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
}

export default function App({ initData }: { initData: RentalReserveInitData }) {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const pickupMode: PickupMode = initData.pickupMode ?? 'member_free';

    const [start, setStart] = useState<Date | null>(null);
    const [end, setEnd] = useState<Date | null>(null);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('18:00');
    const [avail, setAvail] = useState<RentalAvailability | null>(null);
    const [calendar, setCalendar] = useState<RentalCalendar | null>(null);
    const [openingHours, setOpeningHours] = useState<OpeningHours | null>(null);
    const [checking, setChecking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [detail, setDetail] = useState<ReserveItemDetail | null>(null);
    const [termsAccepted, setTermsAccepted] = useState(!!initData.termsAlreadyAccepted);
    const searchQueryId = typeof initData.queryId === 'string' ? initData.queryId : '';
    const reserveAttemptRef = useRef<AttemptKeyState | null>(null);
    const waitlistAttemptKeyRef = useRef<string | null>(null);

    function onTermsChange(checked: boolean): void {
        // Toute interaction conditions invalide la tentative en cours.
        reserveAttemptRef.current = null;
        setTermsAccepted(checked);
    }

    useEffect(() => {
        configureApi(initData.restBase, initData.nonce);
    }, [initData.restBase, initData.nonce]);

    useEffect(() => {
        const from = new Date();
        const to = new Date(from.getTime() + 365 * 86400000);
        fetchCalendar(initData.itemId, `${ymd(from)} 00:00:00`, `${ymd(to)} 00:00:00`)
            .then(setCalendar)
            .catch(() => { /* non bloquant */ });
        fetchOpeningHours().then(setOpeningHours).catch(() => { /* non bloquant */ });
        fetchItem(initData.itemId).then(setDetail).catch(() => { /* non bloquant */ });
    }, [initData.itemId]);

    // Texte récapitulatif des heures d'ouverture (retrait), si configurées.
    const openingText = (() => {
        if (!openingHours?.windows.length) return '';
        const dayNames = [t('rental.calendar.sun'), t('rental.calendar.mon'), t('rental.calendar.tue'), t('rental.calendar.wed'), t('rental.calendar.thu'), t('rental.calendar.fri'), t('rental.calendar.sat')];
        return openingHours.windows
            .map((w) => `${dayNames[w.day_of_week % 7]} ${w.start_time}–${w.end_time}`)
            .join(' · ');
    })();

    const serverStart = serverDateTime(start, pickupMode === 'admin' ? '09:00' : startTime);
    const serverEnd = serverDateTime(end, pickupMode === 'admin' ? '18:00' : endTime);

    useEffect(() => {
        if (!serverStart || !serverEnd || serverEnd <= serverStart) {
            setAvail(null);
            return;
        }
        let cancelled = false;
        setChecking(true);
        fetchAvailability(initData.itemId, serverStart, serverEnd)
            .then((a) => { if (!cancelled) setAvail(a); })
            .catch((e) => { if (!cancelled) notify.error((e as Error).message); })
            .finally(() => { if (!cancelled) setChecking(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverStart, serverEnd, initData.itemId]);

    async function submit() {
        const fingerprint = fingerprintReserveAttempt({
            item_id: initData.itemId,
            start_at: serverStart,
            end_at: serverEnd,
            quantity: 1,
            terms_accepted: termsOk,
            query_id: searchQueryId,
        });
        const attempt = resolveAttemptKey(reserveAttemptRef.current, fingerprint);
        reserveAttemptRef.current = attempt;
        setSubmitting(true);
        try {
            await createReservation(
                initData.itemId,
                serverStart,
                serverEnd,
                1,
                termsOk,
                attempt.key,
                searchQueryId,
            );
            reserveAttemptRef.current = null;
            setDone(true);
            notify.success(t('rental.reserve.success'));
        } catch (e) {
            notify.error((e as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    const isFree = !!initData.isFree || (!!avail?.quote && avail.quote.free);
    // Éligibilité (connecté + autorisé). Défaut true = rétro-compat si non fourni.
    const eligible = initData.canReserve !== false;
    const allowBrowse = initData.allowBrowse !== false;
    const reason = initData.eligibility ?? (initData.isLogged ? 'ok' : 'login');
    const termsOk = !initData.termsRequired || initData.termsAlreadyAccepted || termsAccepted;
    const canSubmit = !!avail && avail.available > 0 && !submitting && eligible && !!start && !!end && termsOk;

    if (done) {
        return (
            <div className="gas-rental-reserve gas-rental-reserve--done">
                <p className="gas-rental-reserve__success">{t('rental.reserve.success_detail')}</p>
            </div>
        );
    }

    // Parcours fermé (config) pour un visiteur sans droit : CTA seul, pas de formulaire.
    if (!allowBrowse && !eligible) {
        return (
            <div className="gas-rental-reserve gas-rental-reserve--gated">
                <p className="gas-rental-reserve__login">
                    {reason === 'login' ? t('rental.reserve.login_required') : t('rental.reserve.members_only')}
                    {reason === 'login' && initData.loginUrl && (
                        <> <a href={initData.loginUrl}>{t('rental.reserve.login_link')}</a></>
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className="gas-rental-reserve">
            <h2 className="gas-rental-reserve__title">{t('rental.reserve.title')}</h2>

            {detail && detail.is_kit && detail.kit_components.length > 0 && (
                <div className="gas-rental-reserve__kit">
                    <p className="gas-rental-reserve__kit-title">📦 {t('rental.reserve.kit_includes')}</p>
                    <ul className="gas-rental-reserve__kit-list">
                        {detail.kit_components.map((c) => (
                            <li key={c.id}>
                                {c.image_url && <img src={c.image_url} alt="" />}
                                <span>{(c.quantity ?? 1)}× {c.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {detail && detail.addons.length > 0 && (
                <div className="gas-rental-reserve__addons">
                    <p className="gas-rental-reserve__addons-title">🧩 {t('rental.reserve.addons')}</p>
                    <ul className="gas-rental-reserve__addons-list">
                        {detail.addons.map((a) => (
                            <li key={a.id}>
                                {a.image_url && <img src={a.image_url} alt="" />}
                                <span>{a.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {calendar && (
                <p className="gas-rental-reserve__capacity">
                    {t('rental.reserve.total_stock', { n: String(calendar.capacity) })}
                </p>
            )}
            {calendar && (
                <AvailabilityCalendar
                    calendar={calendar}
                    start={start}
                    end={end}
                    holidays={openingHours?.holidays ?? []}
                    locale={initData.locale}
                    onSelectRange={(s, e) => { setStart(s); setEnd(e); }}
                />
            )}

            {/* Récapitulatif de la plage + heures, selon le mode de retrait. */}
            <div className="gas-rental-reserve__range">
                <div className="gas-rental-reserve__range-item">
                    <span className="gas-rental-reserve__range-label">{t('rental.reserve.start')}</span>
                    <strong>{fmtDate(start)}</strong>
                    {pickupMode !== 'admin' && (
                        <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} aria-label={t('rental.reserve.start_time')} />
                    )}
                </div>
                <div className="gas-rental-reserve__range-item">
                    <span className="gas-rental-reserve__range-label">{t('rental.reserve.end')}</span>
                    <strong>{fmtDate(end)}</strong>
                    {pickupMode !== 'admin' && (
                        <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} aria-label={t('rental.reserve.end_time')} />
                    )}
                </div>
            </div>

            {pickupMode === 'admin' && (
                <p className="gas-rental-reserve__hint">{t('rental.reserve.pickup_admin_hint')}</p>
            )}
            {pickupMode === 'opening_hours' && (
                <p className="gas-rental-reserve__hint">
                    {t('rental.reserve.pickup_hours_hint')}
                    {openingText && <><br /><strong>{openingText}</strong></>}
                </p>
            )}

            {checking && <p className="gas-rental-reserve__checking">{t('rental.reserve.checking')}</p>}

            {avail && !checking && (start && end) && (
                <div className="gas-rental-reserve__result">
                    {avail.available > 0 ? (
                        <p className="gas-rental-reserve__available">
                            {t('rental.reserve.available', { n: String(avail.available) })}
                        </p>
                    ) : (
                        <div className="gas-rental-reserve__unavailable">
                            <p>{t('rental.reserve.unavailable')}</p>
                            {initData.isLogged && (
                                <button
                                    type="button"
                                    className="gas-button gas-button--outline gas-button--sm"
                                    onClick={async () => {
                                        try {
                                            if (!waitlistAttemptKeyRef.current) {
                                                waitlistAttemptKeyRef.current = newIdempotencyKey();
                                            }
                                            const attemptKey = waitlistAttemptKeyRef.current;
                                            const r = await joinWaitlist(initData.itemId, serverStart, serverEnd, attemptKey);
                                            waitlistAttemptKeyRef.current = null;
                                            notify.success(r.already ? t('rental.waitlist.already') : t('rental.waitlist.joined'));
                                        } catch (e) { notify.error((e as Error).message); }
                                    }}
                                >
                                    {t('rental.waitlist.join')}
                                </button>
                            )}
                        </div>
                    )}

                    {isFree ? (
                        <p className="gas-rental-reserve__price gas-rental-reserve__price--free">{t('rental.public.free')}</p>
                    ) : avail.quote ? (
                        <p className="gas-rental-reserve__price">
                            <span className="gas-rental-reserve__price-label">{t('rental.reserve.total')}</span>
                            {avail.quote.member !== null && avail.quote.member < avail.quote.normal ? (
                                <>
                                    <span className="gas-rental-reserve__price-strike">{avail.quote.normal.toFixed(2)}</span>
                                    <strong>{avail.quote.applied.toFixed(2)} CHF</strong>
                                    <em className="gas-rental-reserve__price-member">{t('rental.reserve.member_price')}</em>
                                </>
                            ) : (
                                <strong>{avail.quote.applied.toFixed(2)} CHF</strong>
                            )}
                        </p>
                    ) : null}
                </div>
            )}

            {eligible ? (
                <>
                    {initData.termsRequired && (
                        <div className="gas-rental-reserve__terms">
                            {initData.termsAlreadyAccepted ? (
                                <p>{t('rental.terms.already_accepted')}</p>
                            ) : (
                                <label>
                                    <input type="checkbox" checked={termsAccepted} onChange={(e) => onTermsChange(e.target.checked)} />
                                    <span>{t(initData.termsMode === 'remember' ? 'rental.terms.accept_remember' : 'rental.terms.accept_once')}</span>
                                </label>
                            )}
                            {initData.termsText ? <details><summary>{t('rental.terms.read')}</summary><p>{initData.termsText}</p></details> : null}
                        </div>
                    )}
                    <button
                        type="button"
                        className="gas-button gas-button--primary gas-rental-reserve__submit"
                        disabled={!canSubmit}
                        onClick={submit}
                    >
                        {submitting ? t('rental.reserve.submitting') : t('rental.reserve.reserve')}
                    </button>
                </>
            ) : (
                <p className="gas-rental-reserve__login">
                    {reason === 'login' ? t('rental.reserve.login_required') : t('rental.reserve.members_only')}
                    {reason === 'login' && initData.loginUrl && (
                        <> <a href={initData.loginUrl}>{t('rental.reserve.login_link')}</a></>
                    )}
                </p>
            )}
        </div>
    );
}
